import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useBookCall } from "@/components/BookCallContext";
import { pickSubIcon } from "@/components/LineIcons";

/**
 * Standard detail page layout for Practices / Products / Industries.
 *
 * Sections top-to-bottom:
 *   1. "Explore <entity>" horizontal pill strip with all siblings + brand
 *      icons. Active item highlighted with accent. Sits ABOVE the hero.
 *   2. Two-column HERO: title/tagline/body/CTA left, landscape image right.
 *   3. Repeated body paragraph.
 *   4. "What we deliver" heading + 2×2 grid of icon-on-left sub-item cards.
 *
 * There are no more list landing pages, every visit to `/practices`,
 * `/products`, `/industries` redirects to the first entity's detail page,
 * and the top nav mega-menu is the always-visible index.
 */
export default function EntityDetailPage({
    kicker,
    title,
    tagline,
    body,
    subItems = [],
    subLabel = "What we deliver",
    image,
    accent = "#8fd0ff",
    highlight,
    bookSource = "entity-detail",
    siblings = [],
    siblingsLabel = "Explore",
    testId,
}) {
    const { openModal } = useBookCall();

    return (
        <div data-testid={testId}>
            {/* ===== SECTION 1: SLIM HORIZONTAL SIBLING STRIP =====
                The "Explore X" text label has been dropped per feedback —
                only the pills remain so users can see and pick siblings
                without a redundant leading label. */}
            {siblings.length > 1 && (
                <div style={switcherBar}>
                    <div className="wrap" style={switcherWrap}>
                        <div
                            className="siblings-row"
                            style={{
                                display: "flex",
                                gap: 8,
                                overflowX: "auto",
                                flex: 1,
                                minWidth: 0,
                                scrollbarWidth: "none",
                            }}
                        >
                            {siblings.map((s) => (
                                <Link
                                    key={s.id}
                                    to={s.path}
                                    style={siblingPillStyle(s.current, accent)}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            color: s.current ? accent : "var(--kl-muted)",
                                            flexShrink: 0,
                                        }}
                                        className="switcher-icon"
                                    >
                                        {s.icon}
                                    </span>
                                    <span
                                        style={{
                                            whiteSpace: "nowrap",
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        {s.name}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== SECTION 2: TWO-COLUMN HERO (tighter, balanced) ===== */}
            <div className="phead" style={{ paddingTop: 40, paddingBottom: 40 }}>
                <div className="wrap" style={{ position: "relative", zIndex: 2 }}>
                    <div className="ed-hero" style={heroGrid}>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                        >
                            <span
                                className="kicker"
                                style={{ color: accent, textTransform: "uppercase" }}
                            >
                                {kicker}
                            </span>
                            <h1 className="h1" style={{ margin: "14px 0 14px" }}>
                                {title}
                            </h1>
                            {tagline ? (
                                <p
                                    className="font-mono-label"
                                    style={{
                                        color: accent,
                                        fontSize: ".82rem",
                                        letterSpacing: ".1em",
                                        textTransform: "uppercase",
                                        marginBottom: 22,
                                        maxWidth: 500,
                                    }}
                                >
                                    {tagline}
                                </p>
                            ) : null}
                            {body ? (
                                <p
                                    style={{
                                        color: "var(--ink)",
                                        fontSize: "1rem",
                                        lineHeight: 1.65,
                                        marginBottom: 28,
                                        maxWidth: 520,
                                    }}
                                >
                                    {body}
                                </p>
                            ) : null}
                            <button
                                className="btn btn-p"
                                onClick={() => openModal({ source: bookSource })}
                            >
                                Book a Demo <ArrowRight size={14} />
                            </button>
                        </motion.div>

                        {image ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.55 }}
                                style={heroImageWrap}
                                aria-hidden
                            >
                                <picture>
                                    <source type="image/webp" srcSet={image} />
                                    <img
                                        src={image}
                                        alt=""
                                        loading="eager"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                </picture>
                            </motion.div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* ===== SECTION 3: SUB-ITEMS ===== */}
            <section style={{ padding: "40px 0 90px" }}>
                <div className="wrap">
                    {subItems.length > 0 && (
                        <div>
                            <h3
                                className="font-mono-label"
                                style={{ color: "var(--dim)", marginBottom: 18 }}
                            >
                                {subLabel}
                            </h3>
                            <div style={subGrid} className="sub-grid">
                                {subItems.map((s) => (
                                    <SubItemCard
                                        key={s.name}
                                        name={s.name}
                                        desc={s.desc}
                                        accent={accent}
                                        highlight={highlight}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <style>{`
                    .siblings-row::-webkit-scrollbar { display: none; }
                    .switcher-icon svg { width: 16px; height: 16px; }
                    @media (max-width: 900px) {
                        .ed-hero { grid-template-columns: 1fr !important; gap: 28px !important; }
                        .sub-grid { grid-template-columns: 1fr !important; }
                    }
                `}</style>
            </section>
        </div>
    );
}

/* ---------- pieces ---------- */

function SubItemCard({ name, desc, accent, highlight }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.38 }}
            style={subCard}
        >
            <div
                aria-hidden
                style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    background: `radial-gradient(65% 65% at 40% 40%, ${accent}30, ${accent}0a 70%)`,
                    border: `1px solid ${accent}40`,
                    color: accent,
                    flexShrink: 0,
                    boxShadow: `0 4px 16px ${accent}22`,
                }}
            >
                {pickSubIcon(name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <b
                    className="font-display"
                    style={{
                        display: "block",
                        marginBottom: 6,
                        fontWeight: 600,
                        fontSize: "1.05rem",
                    }}
                >
                    {renderHighlighted(name, highlight, accent)}
                </b>
                {desc ? (
                    <span
                        style={{
                            color: "var(--kl-muted)",
                            fontSize: ".92rem",
                            lineHeight: 1.55,
                            display: "block",
                        }}
                    >
                        {desc}
                    </span>
                ) : null}
            </div>
        </motion.div>
    );
}

function renderHighlighted(name, highlight, accent) {
    if (!highlight) return name;
    const parts = name.split(/(\s+)/);
    return parts.map((tok, i) => {
        if (tok.trim() === highlight) {
            return (
                <span key={i} style={{ color: accent }}>
                    {tok}
                </span>
            );
        }
        return tok;
    });
}

/* ---------- styles ---------- */

// Slim single-row product switcher — sits directly below the navbar with
// minimal vertical padding and no boxed container.
const switcherBar = {
    borderBottom: "1px solid var(--line)",
    background: "rgba(255,255,255,0.015)",
    padding: "10px 0",
};
const switcherWrap = {
    display: "flex",
    alignItems: "center",
    gap: 16,
};

const heroGrid = {
    display: "grid",
    // ~55% text / ~45% image
    gridTemplateColumns: "1.2fr 1fr",
    gap: 40,
    alignItems: "center",
};

const heroImageWrap = {
    // slimmer landscape so the image doesn't dominate
    aspectRatio: "16 / 10",
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid var(--line)",
    background: "#0d1116",
    maxWidth: 560,
    marginLeft: "auto",
    width: "100%",
};

const siblingPillStyle = (isActive, accent) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px",
    borderRadius: 999,
    border: isActive
        ? `1px solid ${accent}`
        : "1px solid var(--line)",
    background: isActive
        ? `${accent}15`
        : "transparent",
    color: isActive ? "var(--ink)" : "var(--kl-muted)",
    textDecoration: "none",
    fontWeight: 500,
    transition: "all .16s",
    boxShadow: isActive ? `0 0 0 3px ${accent}18` : "none",
    flexShrink: 0,
});

const subGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
};

const subCard = {
    display: "flex",
    alignItems: "flex-start",
    gap: 20,
    padding: "22px 24px",
    borderRadius: 14,
    border: "1px solid var(--line)",
    background: "rgba(255,255,255,0.025)",
    minHeight: 108,
};
